// features/chart-of-accounts/api/exportCoa.js
import axiosInstance from '../../../api/axiosConfig';
import { getAccessToken } from '../../../api/axiosConfig';
import { requireAuthToken } from '../../../utils/authUtils';

/**
 * Read the filename the server chose from Content-Disposition, so the download
 * matches the audit record rather than a name the browser invented.
 */
function filenameFromResponse(response, fallback) {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match ? match[1].trim() : fallback;
}

/**
 * The server sends errors as JSON, but responseType:'blob' means even the error
 * body arrives as a Blob — unwrap it so callers get a real message.
 */
async function messageFromBlobError(error, fallback) {
  const data = error.response?.data;
  if (data instanceof Blob && data.type.includes('json')) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || error.message || fallback;
}

/**
 * Download the Chart of Accounts as CSV and hand the browser the file.
 *
 * clientId is deliberately NOT sent. The server scopes the export from the JWT
 * plus the active-context headers that axiosConfig already attaches; a clientId
 * in the query string would be an untrusted input the server must ignore anyway,
 * so sending one would only imply the caller can choose their own scope.
 * It stays in the signature so call sites read as client-scoped.
 *
 * The filters mirror the list screen so the CSV always contains exactly the rows
 * on display. Defaults match the screen's own defaults, so omitting them exports
 * active accounts of every type.
 *
 * @param {string} [_clientId] active client id — for call-site readability only
 * @param {object} [filters]
 * @param {'active'|'inactive'|'all'} [filters.status='active']
 * @param {string} [filters.type='all'] account type name, or 'all'
 * @param {'all'|'system'|'non-system'} [filters.system='all']
 * @param {string} [filters.search=''] free-text search
 * @returns {Promise<string>} the filename that was saved
 */
export async function exportChartOfAccounts(
  _clientId,
  { status = 'active', type = 'all', system = 'all', search = '' } = {}
) {
  try {
    requireAuthToken(getAccessToken);

    const response = await axiosInstance.get('/chart-of-accounts/export', {
      params: {
        status,
        ...(type && type !== 'all' ? { type } : {}),
        ...(system !== 'all' ? { system } : {}),
        ...(search ? { search } : {}),
      },
      responseType: 'blob',
    });

    const filename = filenameFromResponse(response, 'chart-of-accounts.csv');
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return filename;
  } catch (error) {
    console.error('Chart of Accounts export error:', error);
    throw new Error(await messageFromBlobError(error, 'Failed to export chart of accounts'));
  }
}

export default exportChartOfAccounts;
